import { type FC, useState } from "react";
import { ChevronDown, ChevronUp, TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DefinitionTooltip } from "src/components/common";
import type { EngagementBusinessImpactMetric } from "../../Types/EngagementOverviewTypes";
import { formatNumberDigits, isArabicLanguage, wrapBidiIsolate } from "src/utils/numberFormat";
import { EngagementEmptyState } from "../Shared/SharedParts";
import { getEngagementTooltipInfo } from "../../Utils/EngagementOverviewUtils";

const formatTrendToken = (
  changePercent: number,
  positive: boolean,
  fractionDigits: number,
  language: string,
): string => {
  const digits = formatNumberDigits(
    Math.abs(changePercent),
    { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits },
    language,
  );
  const token = `${positive ? "+" : "-"}${digits}%`;
  return isArabicLanguage(language) ? wrapBidiIsolate(token) : token;
};

export interface BusinessImpactSectionProps {
  metrics: EngagementBusinessImpactMetric[];
  subtitle: string;
  defaultExpanded?: boolean;
  trendFractionDigits?: number;
}

const BusinessImpactSection: FC<BusinessImpactSectionProps> = ({
  metrics,
  subtitle,
  defaultExpanded = true,
  trendFractionDigits = 2,
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const showUpChevron = expanded === defaultExpanded;

  return (
    <section className="engagement-overview-business-impact">
      <div className="engagement-overview-business-impact__header">
        <div className="engagement-overview-business-impact__heading">
          <h2>{t("campaignDashboard.achievementsTitle")}</h2>
          <p>{subtitle}</p>
        </div>
        <button
          type="button"
          className="engagement-overview-business-impact__toggle"
          onClick={() => setExpanded(current => !current)}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t("campaignDashboard.collapseBusinessImpactSummary")
              : t("campaignDashboard.expandBusinessImpactSummary")
          }
        >
          {showUpChevron ? (
            <ChevronUp size={16} aria-hidden />
          ) : (
            <ChevronDown size={16} aria-hidden />
          )}
        </button>
      </div>

      {!metrics?.length ? (
        <EngagementEmptyState message={t("engagementOverview.noBusinessImpactData")} />
      ) : expanded ? (
        <div className="engagement-overview-business-impact__grid" role="list">
          {metrics.map(metric => {
            const changePercent = metric.changePercent;
            const positive = (changePercent ?? 0) >= 0;
            const metricTitle = t(`campaignDashboard.${metric.id}`, {
              defaultValue: metric.title,
            });
            return (
              <div
                key={metric.id}
                className="engagement-overview-business-impact__summary-item"
                role="listitem"
              >
                <div className="engagement-overview-business-impact__summary-value-row">
                  <strong className="engagement-overview-business-impact__summary-value">
                    {metric.value}
                  </strong>
                  <span className="engagement-overview-business-impact__summary-label">
                    {metricTitle}
                  </span>
                  <DefinitionTooltip
                    label={metricTitle}
                    info={getEngagementTooltipInfo(t, metric.id)}
                    formulaTitle={t("engagementOverview.formula")}
                  />
                </div>
                {typeof changePercent === "number" ? (
                  <span
                    className={
                      positive
                        ? "engagement-overview-business-impact__trend engagement-overview-business-impact__trend--positive"
                        : "engagement-overview-business-impact__trend engagement-overview-business-impact__trend--negative"
                    }
                  >
                    {positive ? (
                      <TrendingUp size={14} aria-hidden />
                    ) : (
                      <TrendingDown size={14} aria-hidden />
                    )}
                    {metric.changePercentFormatted ??
                      formatTrendToken(
                        changePercent,
                        positive,
                        trendFractionDigits,
                        i18n.language,
                      )}
                    <span className="engagement-overview-business-impact__trend-copy">
                      {t("engagementOverview.vsLastPeriod")}
                    </span>
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="engagement-overview-business-impact__summary-line" role="list">
          {metrics.map((metric, index) => (
            <div key={metric.id} className="engagement-overview-business-impact__summary-line-item" role="listitem">
              <strong className="engagement-overview-business-impact__summary-line-value">
                {metric.value}
              </strong>
              <span className="engagement-overview-business-impact__summary-line-label">
                {t(`campaignDashboard.${metric.id}`, { defaultValue: metric.title })}
              </span>
              {index < metrics.length - 1 ? (
                <span className="engagement-overview-business-impact__summary-line-separator" aria-hidden>
                  •
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default BusinessImpactSection;
